<template>
  <router-view v-slot="{ Component }">
    <component :is="Component"></component>
  </router-view>
</template>

<script>
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { onMounted, watch } from "vue";
export default {
  name: "App",
  setup() {
    const { locale } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const supportedLanguages = ["en", "zh_Hans", "zh_Hant"];

    const setLanguageFromQuery = () => {
      const langParam = route.query.lang;
      if (langParam && supportedLanguages.includes(langParam)) {
        locale.value = langParam;
      } else {
        locale.value = "en";
      }
    };
    const setupLanguageWatchers = () => {
      // 监听 locale 变化，更新 URL
      watch(locale, (newLocale) => {
        router.replace({
          query: { ...route.query, lang: newLocale },
        });
      });

      // 监听路由变化，更新语言
      watch(
        () => route.query.lang,
        (newLang) => {
          if (newLang && supportedLanguages.includes(newLang)) {
            locale.value = newLang;
          }
        }
      );
    };
    onMounted(() => {
      setLanguageFromQuery();
      setupLanguageWatchers();
    });
  },
};
</script>

<style>
:root {
  --primary: #673ab7;
  --primary-light: #ede7f6;
  --primary-dark: #512da8;
  --text-primary: #212121;
  --text-secondary: #757575;
  --background: #fafafa;
  --card-background: #ffffff;
  --divider: #e0e0e0;
  --shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Roboto", "Noto Sans TC", -apple-system, BlinkMacSystemFont,
    sans-serif;
}

body {
  background-color: var(--background);
  color: var(--text-primary);
  line-height: 1.6;
  padding-bottom: 80px;
}

.app {
  max-width: 100%;
  margin: 0 auto;
  position: relative;
}

/* 通用样式 */
.user-section {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--card-background);
  /*border-bottom: 1px solid var(--divider);*/
}
.serving-section {
  padding: 16px;
  color: var(--text-secondary);
  background: var(--card-background);
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.user-info h3 {
  font-size: 1.1rem;
  margin-bottom: 4px;
}

.user-info p {
  color: var(--text-secondary);
  font-size: 0.9rem;
}
.user-section .recipe-data {
  margin-left: auto;
  display: flex;
}
.recipe-data .recipe-data-item {
  padding: 10px 20px;
  background: var(--primary-light);
  border: 1px solid var(--primary);
  border-radius: 20px;
  margin-left: 5px;
  color: var(--primary);
}
.stats {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: var(--card-background);
  border-bottom: 1px solid var(--divider);
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--primary-light);
  padding: 8px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  color: var(--primary-dark);
}

.section {
  padding: 24px 16px;
  background: var(--card-background);
  margin-bottom: 12px;
  scroll-margin-top: 110px;
}

.description {
  line-height: 1.8;
  margin-bottom: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--divider);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 12px 0;
  border-bottom: 1px solid var(--divider);
}

.tag {
  background: var(--primary-light);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  color: var(--primary-dark);
}

.nutrition-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.nutrition-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--primary-light);
  border-radius: 12px;
}

.nutrition-icon {
  color: var(--primary);
  font-size: 1.2rem;
}

.nutrition-title {
  flex: 1;
  font-weight: 600;
  color: var(--primary-dark);
}

.ingredient-list,
.step-list {
  list-style: none;
}

.ingredient-item,
.step-item {
  padding: 12px 0;
  border-bottom: 1px solid var(--divider);
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.ingredient-title {
  flex: 1;
}
.ingredient-item:last-child,
.step-item:last-child {
  border-bottom: none;
}

.step-number {
  background: var(--primary);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: bold;
}

.step-image {
  width: 100%;
  border-radius: 8px;
  margin-top: 8px;
}

.add-post-button,
.add-comment-button {
  display: block;
  width: 100%;
  background: var(--primary);
  color: white;
  text-align: center;
  padding: 12px;
  border-radius: 24px;
  margin-top: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.add-post-button:hover,
.add-comment-button:hover {
  background: var(--primary-dark);
}
</style>
